import {
  Backdrop,
  Card,
  CardContent,
  CardHeader,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import { Field, Form, Formik, FormikProps } from 'formik';
import React from 'react';
import WO2Button from 'src/common/components/button/Button';
import { PreDefinedTag, ReportFileNameType, ReportJobUpdateModalForm } from '../../store/types';
import * as yup from 'yup';
import { FormikTextField } from 'src/common/components/formik';
import { WO2Select } from 'src/common';

interface Props {
  handleClose: () => void;
  isOpen: boolean;
  isMergeReports: boolean;
  namingTemplate: PreDefinedTag[];
  updateBulkReport: (request: ReportJobUpdateModalForm) => Promise<void>;
}

export const ReportingGenerationUpdateModal = (props: Props): JSX.Element => {
  const customNamingTemplate = props.namingTemplate.map((value) => value.value ?? value.tag).join('_');
  const defaultNamingTemplate = props.isMergeReports
    ? ReportFileNameType.Merged.displayName
    : ReportFileNameType.NonMerged.displayName;
  const theme = useTheme();
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={() => {
        props.handleClose();
      }}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.isOpen}>
        <Card elevation={0} sx={{ zIndex: theme.zIndex.modal + 10 }}>
          <CardHeader title={<Typography variant="h3">Save Job</Typography>} />
          <CardContent>
            <Formik<ReportJobUpdateModalForm>
              enableReinitialize
              initialValues={{
                name: '',
                reportFileNameTemplate: defaultNamingTemplate,
              }}
              validationSchema={yup.object({
                name: yup.string().min(1),
                reportFileNameTemplate: yup.string().min(1),
              })}
              onSubmit={(values: ReportJobUpdateModalForm) => {
                props.updateBulkReport(values);
                props.handleClose();
              }}
            >
              {(formikProps: FormikProps<ReportJobUpdateModalForm>) => (
                <Form>
                  <Grid container spacing={2} direction="column">
                    <Grid item>
                      <Field fullWidth name="name" component={FormikTextField} showRequiredAsterisk label="Job name" />
                    </Grid>

                    <Grid item>
                      <FormControl fullWidth>
                        <InputLabel id="report-file-name">Report File Name</InputLabel>
                        <WO2Select
                          fullWidth
                          labelId="report-file-name"
                          label="Report File Name"
                          value={formikProps.values.reportFileNameTemplate}
                          onChange={(e) => formikProps.setFieldValue('reportFileNameTemplate', e.target.value)}
                        >
                          {props.namingTemplate.length > 0 && (
                            <MenuItem divider value={customNamingTemplate}>
                              {customNamingTemplate}
                            </MenuItem>
                          )}
                          {props.isMergeReports ? (
                            <MenuItem value={ReportFileNameType.Merged.displayName}>
                              {ReportFileNameType.Merged.displayName}
                            </MenuItem>
                          ) : (
                            <MenuItem value={ReportFileNameType.NonMerged.displayName}>
                              {ReportFileNameType.NonMerged.displayName}
                            </MenuItem>
                          )}
                          <MenuItem value={ReportFileNameType.IpsPortfolio.displayName}>
                            {ReportFileNameType.IpsPortfolio.displayName}
                          </MenuItem>
                        </WO2Select>
                      </FormControl>
                    </Grid>
                    <Grid item container spacing={2} mt={2} justifyContent="space-between">
                      <Grid item>
                        <WO2Button variant="outlined" onClick={props.handleClose}>
                          Cancel
                        </WO2Button>
                      </Grid>
                      <Grid item>
                        <WO2Button disabled={!formikProps.isValid} type="submit" variant="contained">
                          Save
                        </WO2Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Fade>
    </Modal>
  );
};
